import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Typography, Paper, Dialog, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getProjects, getCharges, updateCharge, addProject, getClients, getConcepts } from "../../services/firestore";
import ChargeForm from "../../components/ChargeForm";

// PCS Admin React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// PCS Admin React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function Projects() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [charges, setCharges] = useState([]);
  const [clients, setClients] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openProjectForm, setOpenProjectForm] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [newProject, setNewProject] = useState({
    nombre: "",
    cliente: "",
    estado: "En Progreso",
    fecha_inicio: "",
    fecha_fin: "",
  });

  const loadData = async () => {
    try {
      if (id) {
        const [projectsData, allCharges, conceptsData] = await Promise.all([
          getProjects(), 
          getCharges(),
          getConcepts()
        ]);
        const currentProject = projectsData.find((p) => p.id === id);
        setProject(currentProject);
        setConcepts(conceptsData);

        const projectCharges = allCharges.filter((c) => c.proyecto_id === id);
        setCharges(projectCharges);
      } else {
        const [projectsData, clientsData] = await Promise.all([getProjects(), getClients()]);
        setProjects(projectsData);
        setClients(clientsData);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleMarkAsPaid = async (chargeId) => {
    try {
      await updateCharge(chargeId, {
        estado: "Pagado",
        fecha_pago: Date.now(),
      });
      loadData();
    } catch (error) {
      console.error("Error al marcar como pagado:", error);
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      await addProject(newProject);
      setOpenProjectForm(false);
      setNewProject({
        nombre: "",
        cliente: "",
        estado: "En Progreso",
        fecha_inicio: "",
        fecha_fin: "",
      });
      loadData();
    } catch (error) {
      console.error("Error al crear proyecto:", error);
    }
  };

  const handleProjectChange = (e) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const projectColumns = [
    { field: "nombre", headerName: "Nombre", flex: 1 },
    { field: "cliente", headerName: "Cliente", flex: 1 },
    { field: "estado", headerName: "Estado", flex: 1 },
    { field: "fecha_inicio", headerName: "Fecha Inicio", flex: 1 },
    { field: "fecha_fin", headerName: "Fecha Fin", flex: 1 },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1,
      renderCell: (params) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => navigate(`/projects/${params.row.id}`)}
        >
          Ver Detalles
        </MDButton>
      ),
    },
  ];

  const chargeColumns = [
    { 
      field: "concepto_id", 
      headerName: "Concepto", 
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        // Encontrar el concepto correspondiente
        const concept = concepts.find((c) => c.id === params.value);
        if (!concept) return params.value;

        // Obtener todos los cobros del mismo concepto ordenados por fecha
        const conceptCharges = charges
          .filter((c) => c.concepto_id === params.value)
          .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));
        
        // Encontrar el índice del cobro actual
        const chargeIndex = conceptCharges.findIndex((c) => c.id === params.row.id);
        
        return (
          <Typography>
            {concept.nombre} #{chargeIndex + 1}
          </Typography>
        );
      },
    },
    {
      field: "monto",
      headerName: "Monto",
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        return new Intl.NumberFormat("es-MX", {
          style: "currency",
          currency: "MXN",
        }).format(params.value);
      },
    },
    {
      field: "fecha_vencimiento",
      headerName: "Fecha Vencimiento",
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "estado",
      headerName: "Estado",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Typography color={params.value === "Pagado" ? "success.main" : "warning.main"}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <MDBox>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => {
              setSelectedCharge(params.row);
              setOpenForm(true);
            }}
            sx={{ mr: 1 }}
          >
            Editar
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            size="small"
            disabled={params.row.estado === "Pagado"}
            onClick={() => handleMarkAsPaid(params.row.id)}
          >
            Marcar Pagado
          </MDButton>
        </MDBox>
      ),
    },
  ];

  // Ordenar los cobros por estado y fecha
  const sortedCharges = [...charges].sort((a, b) => {
    // Si ambos son del mismo estado, ordenar por fecha
    if (a.estado === b.estado) {
      return new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento);
    }
    // Si uno es pendiente y otro no, el pendiente va primero
    return a.estado === "Pendiente" ? -1 : 1;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Container maxWidth="lg">
          {id ? (
            // Vista de detalle de proyecto
            <>
              <MDBox sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h4">{project?.nombre}</Typography>
                <MDButton variant="gradient" color="info" onClick={() => setOpenForm(true)}>
                  Agregar Cobro
                </MDButton>
              </MDBox>

              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <DataGrid
                  rows={sortedCharges}
                  columns={chargeColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  autoHeight
                  disableSelectionOnClick
                  sortingMode="server"
                  initialState={{
                    sorting: {
                      sortModel: [
                        { field: 'estado', sort: 'asc' },
                        { field: 'fecha_vencimiento', sort: 'asc' }
                      ],
                    },
                  }}
                />
              </Paper>

              <Dialog open={openForm} onClose={() => {
                setOpenForm(false);
                setSelectedCharge(null);
              }} maxWidth="sm" fullWidth>
                <MDBox sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedCharge ? "Editar Cobro" : "Nuevo Cobro"}
                  </Typography>
                  <ChargeForm
                    proyectoId={id}
                    initialValues={selectedCharge}
                    onSaved={() => {
                      setOpenForm(false);
                      setSelectedCharge(null);
                      loadData();
                    }}
                  />
                </MDBox>
              </Dialog>
            </>
          ) : (
            // Vista de lista de proyectos
            <>
              <MDBox sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h4">Proyectos</Typography>
                <MDButton variant="gradient" color="info" onClick={() => setOpenProjectForm(true)}>
                  Nuevo Proyecto
                </MDButton>
              </MDBox>

              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <DataGrid
                  rows={projects}
                  columns={projectColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  autoHeight
                  disableSelectionOnClick
                />
              </Paper>

              <Dialog
                open={openProjectForm}
                onClose={() => setOpenProjectForm(false)}
                maxWidth="sm"
                fullWidth
              >
                <MDBox component="form" onSubmit={handleProjectSubmit} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Nuevo Proyecto
                  </Typography>
                  <MDInput
                    fullWidth
                    label="Nombre"
                    name="nombre"
                    value={newProject.nombre}
                    onChange={handleProjectChange}
                    required
                    sx={{ mb: 2 }}
                  />
                  <MDInput
                    select
                    fullWidth
                    label="Cliente"
                    name="cliente"
                    value={newProject.cliente}
                    onChange={handleProjectChange}
                    required
                    sx={{ mb: 2 }}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.nombre}>
                        {client.nombre}
                      </MenuItem>
                    ))}
                  </MDInput>
                  <MDInput
                    fullWidth
                    label="Fecha Inicio"
                    name="fecha_inicio"
                    type="date"
                    value={newProject.fecha_inicio}
                    onChange={handleProjectChange}
                    required
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <MDInput
                    fullWidth
                    label="Fecha Fin"
                    name="fecha_fin"
                    type="date"
                    value={newProject.fecha_fin}
                    onChange={handleProjectChange}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <MDButton variant="gradient" color="success" type="submit" fullWidth>
                    Crear Proyecto
                  </MDButton>
                </MDBox>
              </Dialog>
            </>
          )}
        </Container>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Projects;
